import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Crosshair, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Field } from '../../../components/Field';
import { Switch } from '../../../components/Switch';
import { EmptyState } from '../../../components/EmptyState';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/Dialog';
import {
    useAttendanceStore, type AttendancePolicy, type EmployeeLocation, type Office, type OfficeInput,
} from '../../../store/attendanceStore';
import { useEmployeeStore } from '../../../store/employeeStore';
import { readGps } from './attendanceUi';

const fieldClass = 'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-50';
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const LOCATION_TYPES = [
    { v: 'OFFICE', l: 'Office' }, { v: 'BRANCH', l: 'Branch' }, { v: 'CLIENT_SITE', l: 'Client site' },
    { v: 'WFH', l: 'Work from home' }, { v: 'REMOTE', l: 'Remote' }, { v: 'OTHER', l: 'Other' },
];
const typeLabel = (t: string) => LOCATION_TYPES.find((x) => x.v === t)?.l ?? t;

/** HR: attendance rules, the location master and who may use which location. */
export function SettingsPanel({ canEdit }: { canEdit: boolean }) {
    return (
        <div className="space-y-4">
            <RulesCard canEdit={canEdit} />
            <LocationsCard canEdit={canEdit} />
            <EmployeeLocationsCard canEdit={canEdit} />
        </div>
    );
}

// ───────────────────────────── rules ─────────────────────────────

function RulesCard({ canEdit }: { canEdit: boolean }) {
    const { policies, fetchPolicies, updatePolicy } = useAttendanceStore();
    const base = policies.find((p) => !p.officeId) || null;
    const [form, setForm] = useState<AttendancePolicy | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { fetchPolicies().catch((e) => toast.error(e.message)); }, [fetchPolicies]);
    useEffect(() => { if (base) setForm(base); }, [base?.id, policies]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!form) return <Card className="p-4 text-sm text-muted-foreground">Loading attendance rules…</Card>;
    const set = <K extends keyof AttendancePolicy>(k: K, v: AttendancePolicy[K]) => setForm({ ...form, [k]: v });
    const hours = (m: number) => Math.round((m / 60) * 100) / 100;

    const save = async () => {
        if (!base) return;
        const patch: Partial<AttendancePolicy> = {};
        (Object.keys(form) as (keyof AttendancePolicy)[]).forEach((k) => {
            if (['id', 'officeId', 'officeName'].includes(k)) return;
            if (JSON.stringify(form[k]) !== JSON.stringify(base[k])) (patch as any)[k] = form[k];
        });
        if (!Object.keys(patch).length) { toast('No changes to save'); return; }
        setBusy(true);
        try { await updatePolicy(patch); toast.success('Attendance rules saved'); } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
    };

    const toggle = (k: keyof AttendancePolicy, label: string, hint?: string) => (
        <label className="flex items-start justify-between gap-4 py-2">
            <span><span className="block text-sm text-foreground">{label}</span>{hint && <span className="block text-xs text-muted-foreground">{hint}</span>}</span>
            <Switch checked={Boolean(form[k])} disabled={!canEdit} onCheckedChange={(v) => set(k, v as any)} />
        </label>
    );

    return (
        <Card>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                <div>
                    <h2 className="font-display text-card-title text-foreground">Attendance rules</h2>
                    <p className="text-xs text-muted-foreground">Apply to everyone unless a location has its own rules. Every change is audited.</p>
                </div>
                {canEdit && <Button onClick={save} isLoading={busy}>Save rules</Button>}
            </div>
            <div className="grid gap-6 p-4 lg:grid-cols-2">
                <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-foreground">Working day</h3>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Expected hours per day">
                            <input className={fieldClass} type="number" min={1} max={24} step={0.5} disabled={!canEdit}
                                value={hours(form.expectedWorkMinutes)} onChange={(e) => set('expectedWorkMinutes', Math.round(Number(e.target.value) * 60))} />
                        </Field>
                        <Field label="Half day below (hours)" hint="Less than this counts as absent.">
                            <input className={fieldClass} type="number" min={0} max={24} step={0.5} disabled={!canEdit}
                                value={hours(form.halfDayMinMinutes)} onChange={(e) => set('halfDayMinMinutes', Math.round(Number(e.target.value) * 60))} />
                        </Field>
                        <Field label="Default start time" hint="Used for lateness when no shift is assigned.">
                            <input className={fieldClass} type="time" disabled={!canEdit} value={form.defaultStartTime?.slice(0, 5) || ''}
                                onChange={(e) => set('defaultStartTime', e.target.value || null)} />
                        </Field>
                        <Field label="Close forgotten sessions after (hours)">
                            <input className={fieldClass} type="number" min={1} max={48} disabled={!canEdit}
                                value={hours(form.maxSessionMinutes)} onChange={(e) => set('maxSessionMinutes', Math.round(Number(e.target.value) * 60))} />
                        </Field>
                        <Field label="Late after (grace, minutes)">
                            <input className={fieldClass} type="number" min={0} max={600} disabled={!canEdit}
                                value={form.lateGraceMinutes} onChange={(e) => set('lateGraceMinutes', Number(e.target.value))} />
                        </Field>
                        <Field label="Early check-out grace (minutes)">
                            <input className={fieldClass} type="number" min={0} max={600} disabled={!canEdit}
                                value={form.earlyCheckoutGraceMinutes} onChange={(e) => set('earlyCheckoutGraceMinutes', Number(e.target.value))} />
                        </Field>
                    </div>
                    {/* <Field label="Week-off days">
                        <div className="flex flex-wrap gap-1.5">
                            {DAYS.map((d, i) => {
                                const on = form.weekOffDays.includes(i);
                                return (
                                    <button key={d} type="button" disabled={!canEdit}
                                        onClick={() => set('weekOffDays', on ? form.weekOffDays.filter((x) => x !== i) : [...form.weekOffDays, i].sort())}
                                        className={`h-8 rounded-md border px-3 text-sm transition-colors disabled:opacity-60 ${on ? 'border-primary bg-primary/10 text-primary-strong' : 'border-border bg-card text-muted-foreground hover:bg-secondary'}`}>
                                        {d}
                                    </button>
                                );
                            })}
                        </div>
                    </Field> */}
                </div>

                <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-foreground">Location</h3>
                    <div className="divide-y divide-border">
                        {toggle('locationValidationEnabled', 'Check location on check-in/out')}
                        {toggle('allowWfh', 'Allow check-in from anywhere', 'Outside an office, the check-in is recorded as Home with the place detected from GPS')}
                        {toggle('requireAdjustmentApproval', 'Adjustments need approval')}
                    </div>
                    <Field label="When the location can't be verified" hint="Outside the allowed area, weak GPS or no GPS.">
                        <div className="grid grid-cols-3 gap-1.5">
                            {([['ALLOW', 'Allow'], ['FLAG', 'Allow & flag for review'], ['BLOCK', 'Block']] as const).map(([v, l]) => (
                                <button key={v} type="button" disabled={!canEdit} onClick={() => set('outsideLocationAction', v)}
                                    className={`min-h-9 rounded-md border px-2 py-1 text-sm transition-colors disabled:opacity-60 ${form.outsideLocationAction === v ? 'border-primary bg-primary/10 text-primary-strong' : 'border-border bg-card text-muted-foreground hover:bg-secondary'}`}>
                                    {l}
                                </button>
                            ))}
                        </div>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Weakest GPS accepted (m)" hint="Readings less accurate than this are flagged.">
                            <input className={fieldClass} type="number" min={5} max={5000} disabled={!canEdit}
                                value={form.maxGpsAccuracyM} onChange={(e) => set('maxGpsAccuracyM', Number(e.target.value))} />
                        </Field>
                        <Field label="Edge tolerance (m)" hint="Extra margin at the boundary for GPS error.">
                            <input className={fieldClass} type="number" min={0} max={500} disabled={!canEdit}
                                value={form.boundaryToleranceCapM} onChange={(e) => set('boundaryToleranceCapM', Number(e.target.value))} />
                        </Field>
                    </div>
                    <Field label="Time zone">
                        <input className={fieldClass} disabled={!canEdit} value={form.timezone} onChange={(e) => set('timezone', e.target.value)} />
                    </Field>
                </div>
            </div>
        </Card>
    );
}

// ───────────────────────────── locations (offices) ─────────────────────────────

const EMPTY_OFFICE: OfficeInput = {
    name: '', code: '', locationType: 'OFFICE', address: '', city: '', state: '', country: 'India', postalCode: '',
    timezone: 'Asia/Kolkata', latitude: NaN, longitude: NaN, radius: 100, status: 'Active',
};

function LocationsCard({ canEdit }: { canEdit: boolean }) {
    const { offices, fetchOffices, saveOffice } = useAttendanceStore();
    const [editor, setEditor] = useState<{ id?: string; form: OfficeInput } | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { fetchOffices(); }, [fetchOffices]);

    const toInput = (o: Office): OfficeInput => ({ ...o });
    const set = (k: keyof OfficeInput, v: any) => editor && setEditor({ ...editor, form: { ...editor.form, [k]: v } });

    const useHere = async () => {
        try {
            const g = await readGps();
            if (editor) setEditor({ ...editor, form: { ...editor.form, latitude: Number(g.latitude.toFixed(7)), longitude: Number(g.longitude.toFixed(7)) } });
            toast.success(`Location captured (±${Math.round(g.accuracy)} m)`);
        } catch (e: any) { toast.error(e.message); }
    };

    const save = async () => {
        if (!editor) return;
        const f = editor.form;
        if (!f.name.trim()) { toast.error('Name is required.'); return; }
        if (!Number.isFinite(f.latitude) || !Number.isFinite(f.longitude)) { toast.error('Enter the latitude and longitude.'); return; }
        setBusy(true);
        try {
            await saveOffice(f, editor.id);
            toast.success(editor.id ? 'Location updated' : 'Location added');
            setEditor(null);
        } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
    };

    return (
        <Card>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                <div>
                    <h2 className="font-display text-card-title text-foreground">Offices & locations</h2>
                    <p className="text-xs text-muted-foreground">Check-ins are validated against these. Past check-ins keep the location as it was at the time.</p>
                </div>
                {canEdit && <Button variant="secondary" onClick={() => setEditor({ form: { ...EMPTY_OFFICE } })}><Plus /> Add location</Button>}
            </div>
            {offices.length === 0 ? (
                <EmptyState className="m-4 border-0" icon={<MapPin />} title="No locations yet" description="Add your offices and branches with their coordinates." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b border-border bg-secondary/50 text-left text-xs text-muted-foreground">
                            <tr>
                                <th className="px-4 py-2 font-semibold">Location</th>
                                <th className="px-4 py-2 font-semibold">Type</th>
                                <th className="px-4 py-2 font-semibold">City</th>
                                <th className="px-4 py-2 font-semibold">Coordinates</th>
                                <th className="px-4 py-2 text-right font-semibold">Radius</th>
                                <th className="px-4 py-2 font-semibold">Status</th>
                                <th className="px-4 py-2" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {offices.map((o) => (
                                <tr key={o.id} className="hover:bg-secondary/30">
                                    <td className="px-4 py-2"><p className="font-medium text-foreground">{o.name}</p>{o.code && <p className="text-xs text-muted-foreground">{o.code}</p>}</td>
                                    <td className="px-4 py-2 text-muted-foreground">{typeLabel(o.locationType)}</td>
                                    <td className="px-4 py-2 text-muted-foreground">{o.city || '—'}</td>
                                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{o.latitude.toFixed(5)}, {o.longitude.toFixed(5)}</td>
                                    <td className="px-4 py-2 text-right tabular-nums text-foreground">{o.radius} m</td>
                                    <td className="px-4 py-2"><Badge variant={o.status === 'Active' ? 'success' : 'muted'}>{o.status}</Badge></td>
                                    <td className="px-4 py-2 text-right">
                                        {canEdit && <Button size="icon-sm" variant="ghost" aria-label={`Edit ${o.name}`} onClick={() => setEditor({ id: o.id, form: toInput(o) })}><Pencil /></Button>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editor?.id ? 'Edit location' : 'Add location'}</DialogTitle>
                        <DialogDescription>Use the exact coordinates of the entrance or building centre. Changing them does not alter past check-ins.</DialogDescription>
                    </DialogHeader>
                    {editor && (
                        <div className="grid gap-3 sm:grid-cols-2">
                            <Field label="Name" required className="sm:col-span-2"><input className={fieldClass} value={editor.form.name} onChange={(e) => set('name', e.target.value)} maxLength={255} /></Field>
                            <Field label="Code"><input className={fieldClass} value={editor.form.code || ''} onChange={(e) => set('code', e.target.value)} maxLength={30} placeholder="e.g. CHN-01" /></Field>
                            <Field label="Type">
                                <select className={fieldClass} value={editor.form.locationType} onChange={(e) => set('locationType', e.target.value)}>
                                    {LOCATION_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                                </select>
                            </Field>
                            <Field label="Address" className="sm:col-span-2"><input className={fieldClass} value={editor.form.address || ''} onChange={(e) => set('address', e.target.value)} /></Field>
                            <Field label="City"><input className={fieldClass} value={editor.form.city || ''} onChange={(e) => set('city', e.target.value)} /></Field>
                            <Field label="State"><input className={fieldClass} value={editor.form.state || ''} onChange={(e) => set('state', e.target.value)} /></Field>
                            <Field label="Country"><input className={fieldClass} value={editor.form.country || ''} onChange={(e) => set('country', e.target.value)} /></Field>
                            <Field label="Postal code"><input className={fieldClass} value={editor.form.postalCode || ''} onChange={(e) => set('postalCode', e.target.value)} /></Field>
                            <Field label="Latitude" required><input className={fieldClass} type="number" step="0.0000001" value={Number.isFinite(editor.form.latitude) ? editor.form.latitude : ''} onChange={(e) => set('latitude', e.target.value === '' ? NaN : Number(e.target.value))} /></Field>
                            <Field label="Longitude" required><input className={fieldClass} type="number" step="0.0000001" value={Number.isFinite(editor.form.longitude) ? editor.form.longitude : ''} onChange={(e) => set('longitude', e.target.value === '' ? NaN : Number(e.target.value))} /></Field>
                            <div className="sm:col-span-2">
                                <Button size="sm" variant="ghost" type="button" onClick={useHere}><Crosshair /> Use my current location</Button>
                            </div>
                            <Field label="Allowed radius (m)" hint="10 – 5000 m"><input className={fieldClass} type="number" min={10} max={5000} value={editor.form.radius} onChange={(e) => set('radius', Number(e.target.value))} /></Field>
                            <Field label="Time zone"><input className={fieldClass} value={editor.form.timezone || ''} onChange={(e) => set('timezone', e.target.value)} /></Field>
                            <label className="flex items-center justify-between gap-3 sm:col-span-2">
                                <span className="text-sm text-foreground">Active <span className="text-xs text-muted-foreground">(inactive locations stop validating check-ins but keep their history)</span></span>
                                <Switch checked={editor.form.status !== 'Inactive'} onCheckedChange={(v) => set('status', v ? 'Active' : 'Inactive')} />
                            </label>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setEditor(null)} disabled={busy}>Cancel</Button>
                        <Button onClick={save} isLoading={busy}>{editor?.id ? 'Save changes' : 'Add location'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}

// ───────────────────────────── employee → locations ─────────────────────────────

const KIND_LABEL: Record<EmployeeLocation['kind'], string> = { PRIMARY: 'Primary', ALLOWED: 'Also allowed', WFH: 'Approved WFH', CLIENT_SITE: 'Client site' };

function EmployeeLocationsCard({ canEdit }: { canEdit: boolean }) {
    const { offices, fetchEmployeeLocations, addEmployeeLocation, removeEmployeeLocation } = useAttendanceStore();
    const { fetchAllEmployees } = useEmployeeStore();
    const [employees, setEmployees] = useState<{ id: string; name: string; department: string }[]>([]);
    const [employeeId, setEmployeeId] = useState('');
    const [list, setList] = useState<EmployeeLocation[]>([]);
    const [form, setForm] = useState({ kind: 'PRIMARY', officeId: '', label: '', latitude: '', longitude: '', radiusM: '150', validFrom: '', validTo: '' });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        fetchAllEmployees()
            .then((rows) => setEmployees(rows.filter((e) => e.status !== 'terminated' && e.status !== 'resigned')
                .map((e) => ({ id: e.id, name: e.displayName, department: e.department || '' })).sort((a, b) => a.name.localeCompare(b.name))))
            .catch(() => toast.error('Could not load employees'));
    }, [fetchAllEmployees]);

    const load = (id = employeeId) => { if (id) fetchEmployeeLocations(id).then(setList).catch((e) => toast.error(e.message)); };
    useEffect(() => { setList([]); load(employeeId); }, [employeeId]); // eslint-disable-line react-hooks/exhaustive-deps

    const activeOffices = useMemo(() => offices.filter((o) => o.status === 'Active'), [offices]);
    const isWfhCoords = form.kind === 'WFH' && !form.officeId;

    const add = async () => {
        if (!employeeId) return;
        if (!isWfhCoords && !form.officeId) { toast.error('Choose a location.'); return; }
        if (isWfhCoords && (!form.latitude || !form.longitude)) { toast.error('Enter the home location coordinates, or pick a WFH location.'); return; }
        setBusy(true);
        try {
            await addEmployeeLocation(employeeId, {
                kind: form.kind, officeId: form.officeId || null, label: form.label || null,
                latitude: isWfhCoords ? Number(form.latitude) : null, longitude: isWfhCoords ? Number(form.longitude) : null,
                radiusM: isWfhCoords ? Number(form.radiusM) : null, validFrom: form.validFrom || null, validTo: form.validTo || null,
            });
            toast.success('Location assigned');
            setForm({ ...form, officeId: '', label: '', latitude: '', longitude: '' });
            load();
        } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
    };

    const remove = async (id: string) => {
        try { await removeEmployeeLocation(id); toast.success('Removed'); load(); } catch (e: any) { toast.error(e.message); }
    };

    return (
        <Card>
            <div className="border-b border-border px-4 py-3">
                <h2 className="font-display text-card-title text-foreground">Where each employee may check in</h2>
                <p className="text-xs text-muted-foreground">Until you assign locations, an employee may check in at any active office or branch.</p>
            </div>
            <div className="space-y-4 p-4">
                <Field label="Employee">
                    <select className={fieldClass} value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                        <option value="">Choose an employee</option>
                        {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.department ? ` · ${e.department}` : ''}</option>)}
                    </select>
                </Field>

                {employeeId && (
                    <>
                        {list.filter((l) => l.isActive).length === 0 ? (
                            <p className="text-sm text-muted-foreground">No locations assigned — any active office or branch is accepted.</p>
                        ) : (
                            <ul className="divide-y divide-border rounded-lg border border-border">
                                {list.filter((l) => l.isActive).map((l) => (
                                    <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                                        <div className="min-w-0">
                                            <p className="text-sm text-foreground"><Badge variant={l.kind === 'PRIMARY' ? 'primary' : 'muted'}>{KIND_LABEL[l.kind]}</Badge>
                                                <span className="ml-2">{l.officeName || l.label || 'Home location'}</span></p>
                                            <p className="text-xs text-muted-foreground">
                                                From {l.validFrom}{l.validTo ? ` to ${l.validTo}` : ''}{l.approvedByName ? ` · approved by ${l.approvedByName}` : ''}
                                                {l.latitude !== null ? ` · ${l.latitude.toFixed(5)}, ${l.longitude?.toFixed(5)} (${l.radius} m)` : ''}
                                            </p>
                                        </div>
                                        {canEdit && <Button size="icon-sm" variant="ghost" aria-label="Remove" onClick={() => remove(l.id)}><Trash2 /></Button>}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {canEdit && (
                            <div className="grid gap-3 rounded-lg border border-dashed border-border p-3 sm:grid-cols-3">
                                <Field label="Kind">
                                    <select className={fieldClass} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                                        {Object.entries(KIND_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </select>
                                </Field>
                                <Field label="Location" className="sm:col-span-2">
                                    <select className={fieldClass} value={form.officeId} onChange={(e) => setForm({ ...form, officeId: e.target.value })}>
                                        <option value="">{form.kind === 'WFH' ? "Employee's home (enter coordinates)" : 'Choose a location'}</option>
                                        {activeOffices.map((o) => <option key={o.id} value={o.id}>{o.name}{o.city ? ` · ${o.city}` : ''}</option>)}
                                    </select>
                                </Field>
                                {isWfhCoords && (
                                    <>
                                        <Field label="Label"><input className={fieldClass} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Home, Anna Nagar" /></Field>
                                        <Field label="Latitude"><input className={fieldClass} type="number" step="0.0000001" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} /></Field>
                                        <Field label="Longitude"><input className={fieldClass} type="number" step="0.0000001" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} /></Field>
                                        <Field label="Radius (m)"><input className={fieldClass} type="number" min={10} max={5000} value={form.radiusM} onChange={(e) => setForm({ ...form, radiusM: e.target.value })} /></Field>
                                    </>
                                )}
                                <Field label="From (optional)"><input className={fieldClass} type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} /></Field>
                                <Field label="Until (optional)"><input className={fieldClass} type="date" value={form.validTo} onChange={(e) => setForm({ ...form, validTo: e.target.value })} /></Field>
                                <div className="flex items-end sm:col-span-3">
                                    <Button variant="secondary" onClick={add} isLoading={busy}><Plus /> Assign location</Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Card>
    );
}
